import { useMemo } from 'react';
import { Typography, TypographyProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { differenceInHours, differenceInMinutes, differenceInSeconds, isTomorrow } from 'date-fns';
import { formatDate } from '@razz-team/utils';

export function useRelativeDateTime(
  value: string | number | Date | undefined | null,
  baseDate: Date = new Date(),
) {
  const { t, i18n } = useTranslation();

  const text = useMemo(() => {
    if (!value) {
      return undefined;
    }

    const seconds = differenceInSeconds(value, baseDate);
    const minutes = Math.ceil(seconds / 60);
    if (minutes < 5) {
      return t('instantly');
    }

    const hours = Math.ceil(seconds / 3600);
    if (hours >= 24) {
      if (isTomorrow(value)) {
        return t('tomorrow');
      }

      return formatDate(value, 'date');
    }

    const rtf = new Intl.RelativeTimeFormat(i18n.language);
    if (minutes < 60) {
      return rtf.format(differenceInMinutes(value, baseDate), 'minute');
    }

    return rtf.format(differenceInHours(value, baseDate), 'hour');
  }, [value, baseDate, t, i18n.language]);

  return text;
}

export interface RelativeDateTimeProps extends Omit<TypographyProps, 'children'> {
  value: string | number | Date | undefined | null;
  baseDate?: Date;
}

export function RelativeDateTime({
  value,
  baseDate = new Date(),
  ...props
}: RelativeDateTimeProps) {
  const text = useRelativeDateTime(value, baseDate);
  return <Typography {...props}>{text}</Typography>;
}
